import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { type FC } from 'react';

import { useCapacityTestPreflight } from '../../../api/index.js';

interface CapacityTestConfirmDialogProps {
  open: boolean;
  routerId: string;
  starting: boolean;
  startError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function cameraSummary(axisCameras: number, reolinkCameras: number): string {
  const parts = [
    axisCameras > 0 ? `${axisCameras} Axis` : null,
    reolinkCameras > 0 ? `${reolinkCameras} Reolink` : null,
  ].filter(Boolean);
  return `Tests ${parts.join(' and ')} camera${axisCameras + reolinkCameras === 1 ? '' : 's'} on the active SIM.`;
}

const CapacityTestConfirmDialog: FC<CapacityTestConfirmDialogProps> = ({
  open,
  routerId,
  starting,
  startError,
  onConfirm,
  onCancel,
}) => {
  const { data: preflight, isLoading } = useCapacityTestPreflight(routerId, open);
  const blocked = Boolean(preflight?.blockedReason);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Run a network test?</DialogTitle>
      <DialogContent>
        {isLoading || !preflight ? (
          <CircularProgress size={24} />
        ) : (
          <Stack spacing={1.5}>
            {preflight.blockedReason ? <Alert severity="error">{preflight.blockedReason}</Alert> : null}
            <Typography variant="body1">
              {`${cameraSummary(preflight.axisCameras, preflight.reolinkCameras)} All video from this router stops ` +
                `for about ${preflight.estimatedMinutes} minutes and comes back on its own when the test ends.`}
            </Typography>
            {preflight.liveViewers > 0 ? (
              <Alert severity="warning">
                {`${preflight.liveViewers} ${preflight.liveViewers === 1 ? 'person is' : 'people are'} ` +
                  'watching this router\'s cameras right now. Their video will stop.'}
              </Alert>
            ) : null}
            {preflight.inMonitoringHours ? (
              <Alert severity="warning">The patient is in monitoring hours. Monitors will lose video during the test.</Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {`Uses up to about ${preflight.estimatedMegabytes} MB of cellular data.`}
            </Typography>
            {startError ? <Alert severity="error">{startError}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="warning"
          loading={starting}
          disabled={isLoading || blocked}
          onClick={onConfirm}
        >
          Pause video and test
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CapacityTestConfirmDialog;
